import { Url } from '~/types/types';
import { getFrontendConfig } from './getConfig';

/**
 * @file links.ts
 *
 * @fileoverview Helper functions for Drupal links.
 */
let routesConfig = getFrontendConfig('routes');
let appConfig = getFrontendConfig('app');

/**
 * Checks that a string is included in a given rule.
 * Supports wildcards with *.
 *
 * e.g. Checks that /jobs/full-time matches /jobs*
 */
export const matchRuleShort = (str, rule) => {
  var escapeRegex = (str) => str.replace(/([.*+?^=!:${}()|\[\]\/\\])/g, '\\$1');
  return new RegExp(
    '^' + rule.split('*').map(escapeRegex).join('.*') + '$',
  ).test(str);
};

/**
 * Replaces Drupal's internal path with nothing so that it can be
 * rendered by nextjs.
 *
 * @param url
 *   The url to be replaced.
 * @returns
 *  The url with no internal path in it.
 */
export const drupalRelativePath = (url: string): string => {
  return url.replace('internal:', '');
};

/**
 * For a translated link return the original link in the default language.
 */
export const getOriginalLink = (url: string, locale?: string) => {
  let originalUrl = url;

  if (locale && appConfig.public.PATH_TRANSLATIONS_ACTIVE) {
    const localeToUse =
      appConfig.public.PATH_TRANSLATIONS_ACTIVE?.[locale] || locale;

    routesConfig.translatedRoutes?.[localeToUse] &&
      routesConfig.translatedRoutes?.[localeToUse].forEach((route) => {
        if (matchRuleShort(url, route.newPathname)) {
          originalUrl = url.replace(
            route.newPathname.replace('*', ''),
            route.pathname.replace('*', ''),
          );
          return;
        }
      });
  }

  return originalUrl;
};

/**
 * For all translations return the current link in that language
 */
export const getTranslatedLink = (url: string, locale?: string) => {
  let translatedUrl = url;

  if (locale && appConfig.public.PATH_TRANSLATIONS_ACTIVE) {
    const localeToUse =
      appConfig.public.PATH_TRANSLATIONS_ACTIVE?.[locale] || locale;

    routesConfig.translatedRoutes?.[localeToUse] &&
      routesConfig.translatedRoutes?.[localeToUse].forEach((route) => {
        if (matchRuleShort(url, route.pathname)) {
          translatedUrl = url.replace(
            route.pathname.replace('*', ''),
            route.newPathname.replace('*', ''),
          );
          return;
        }
      });
  }

  return translatedUrl;
};

/**
 * On multilingual sites, sets the locale of a URL on the site's host.
 */
export const withLocalePrefix = (
  url: string,
  host: string | undefined,
  locales: string[],
  locale: string | undefined,
): string => {
  if (!url || !host || !locale || locales.length < 2) {
    return url;
  }

  const withoutProtocol = (value: string) => value.replace(/^https?:\/\//, '');
  const base = withoutProtocol(host).replace(/\/$/, '');
  const target = withoutProtocol(url);
  const rest = target.slice(base.length);
  if (!target.startsWith(base) || !/^($|[/?#])/.test(rest)) {
    return url;
  }

  const head = url.slice(0, url.length - rest.length);
  const segments = rest.match(/^\/([^/?#]*)(.*)$/);
  const path = segments && locales.includes(segments[1]) ? segments[2] : rest;
  return `${head}/${locale}${path.replace(/^\/(?=$|[?#])/, '')}`;
};

/**
 * Checks if given link matches current URL,
 * or if the link path starts with the same
 * path part as current URL path.
 *
 * @param {Url} url
 * @param {string} path
 * @returns
 *  Boolean value indicating if the given menu item
 *  is active.
 */
export function isUrlActive(url: Url, path: string): boolean {
  // Return false for the external links
  if (url.path.startsWith('http')) {
    return false;
  }

  // Remove query parameters from the URL.
  const urlPath = url.path.split('?')[0];
  const currentPath = path.split('?')[0];

  if (urlPath === currentPath) {
    return true;
  }

  // Check if the current path starts with the URL path.
  return currentPath.startsWith(urlPath + '/');
}

/**
 * Returns the service from the given URL.
 */
export const extractServiceFromUrl = (url: string): string => {
  if (url) {
    const urlObj = new URL(url);
    const parts = urlObj.hostname.split('.');
    return parts[parts.length - 2];
  }

  return '';
};
